import { create } from 'zustand'

interface PageState {
  keys: string[]
  value: number
  all: boolean
  tableKeyName?: string
}

export interface TableSelectorState {
  pages: PageState[]
  toggleAllSelectedItems: (currentPage: number, itemsSelected?: string[], tableKeyName?: string) => void
  addRemoveItem: (key: string, currentPage: number, tableKeyName?: string) => void
  isSelected: (key: string, currentPage: number, tableKeyName?: string) => boolean
  getPage: (currentPage: number, tableKeyName?: string) => PageState
  clear: () => void
  clearCurrentPage: (currentPage: number, tableKeyName?: string) => void
}

const currentTableKeyName = (x: PageState, ref?: string) => x.tableKeyName === ref

export const useTableSelector = create<TableSelectorState>((set, get) => ({
  pages: [],
  addRemoveItem: (key, currentPage, tableKeyName) => {
    const { pages } = get()

    const formattedPage = currentPage + 1
    const current = pages.find((x) => x.value === formattedPage && currentTableKeyName(x, tableKeyName))
    const item: PageState = { value: formattedPage, keys: [], all: false, tableKeyName }
    const list = pages.filter((x) => x.value !== formattedPage && currentTableKeyName(x, tableKeyName))

    if (!current) {
      item.keys = [key]
      set({ pages: [...pages, item] })
      return
    }

    if (!current.keys.includes(key)) {
      item.keys = [...current.keys, key]
      set({ pages: [...list, item] })
      return
    }

    const itemsFiltered = current.keys.filter((x) => x !== key)
    item.keys = itemsFiltered

    set({ pages: [...list, item] })
  },
  toggleAllSelectedItems: (currentPage, keys, tableKeyName) => {
    const { pages } = get()

    const formattedPage = currentPage + 1
    const current = pages.find((x) => x.value === formattedPage && currentTableKeyName(x, tableKeyName))
    const list = pages.filter((x) => x.value !== formattedPage || !currentTableKeyName(x, tableKeyName))

    const item = current ?? { value: formattedPage, keys: [], all: false, tableKeyName }
    item.keys = keys ?? []
    item.all = item.keys.length > 0

    set({ pages: [...list, item] })
  },
  isSelected: (key, currentPage, tableKeyName) => {
    const { pages } = get()
    const formattedPage = currentPage + 1
    return !!pages.find(
      (x) => x.value === formattedPage && x.keys.includes(key) && currentTableKeyName(x, tableKeyName)
    )
  },
  getPage: (currentPage, tableKeyName) => {
    const { pages } = get()
    const formattedPage = currentPage + 1
    return (
      pages.find((x) => x.value === formattedPage && currentTableKeyName(x, tableKeyName)) ?? {
        all: false,
        keys: [],
        value: formattedPage,
        tableKeyName,
      }
    )
  },
  clear: () => {
    set({ pages: [] })
  },
  clearCurrentPage: (currentPage, tableKeyName) => {
    const { pages } = get()
    const formattedPage = currentPage + 1
    const list = pages.filter((x) => x.value !== formattedPage || !currentTableKeyName(x, tableKeyName))
    set({ pages: list })
  },
}))
